import { DialogActions, Divider, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import DeleteCircle from "assets/DeleteCircle";
import { setInncodes } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import AsyncAutocomplete from "components/common/Autocomplete";
import AutocompleteInncode from "components/common/AutocompleteInncode";
import Button from "components/common/Button";
import { clearFilters, setSavedFilterByType } from "components/common/Filters/Filters.slice";
import ButtonLink from "components/common/Link";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import { setCreatedSuccess, setSelectedList, setSelectedListCategory } from "components/features/Corp/CorpUser.Slice";
import { useTranslate } from "hooks/useTranslate";
import { cloneDeep } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { SME_PAGE_SIZE, messages } from "utils/CommonConstants";
import { responseTypes } from "utils/Types";
import { fetchMyListDetails, fetchMyLists, updateInncodeUploadList } from "./sme.slice";

interface Props {
  invalidInncodes: string[];
  uploadFileDetails: any;
  setInvalidInncodes?: any;
}

const ImportUpdateDialog = (props: Props) => {
  const translate = useTranslate();
  const { invalidInncodes, uploadFileDetails, setInvalidInncodes } = props;
  const { isMobileScreen } = useContext(ThemeContext);
  let [, setSearchParams] = useSearchParams();
  const [openDialog, setOpenDialog] = useState<any>(false);
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<any[]>([]);
  const savedFilter = useAppSelector((state) => state.filters?.savedFilter);

  const dialogContentStyle = isMobileScreen
    ? {
        margin: "0px",
        padding: "0 16px",
        overflow: "auto"
      }
    : {
        margin: "20px 32px",
        padding: "0",

        "-ms-overflow-style": "none",
        "scrollbar-width": "none",

        "&::-webkit-scrollbar": {
          display: "none"
        }
      };

  useEffect(() => {
    setValues(invalidInncodes);
    if (invalidInncodes.length > 0) setOpenDialog(true);
  }, [invalidInncodes]);

  const handleClose = () => {
    setValues([]);
    setOpenDialog(false);
    setInvalidInncodes && setInvalidInncodes([]);
    dispatch(setInncodes([]));
  };

  const handleChange = (v: any, index: any) => {
    const tempValues = values.map((oldVal, i) => {
      if (i === index) {
        return { ...oldVal, value: { innCode: v?.innCode, email: null, hltId: null } };
      } else {
        return oldVal;
      }
    });

    setValues(tempValues);
    dispatch(setInncodes([]));
  };

  const checkIfListExist = (filterList: any, filterRes: any) => {
    const indexById = filterList.findIndex((obj: any) => obj.id === filterRes.id);
    if (indexById > -1) {
      filterList.splice(indexById, 1, filterRes);
      const consultantListPayload = {
        data: filterList,
        type: "consultant"
      };
      dispatch(setSavedFilterByType(consultantListPayload));
    } else {
      const lastIndex = filterList.findLastIndex((item: any) => item.isFlagged === "Y");
      if (lastIndex > -1) {
        filterList.splice(lastIndex + 1, 0, filterRes);
      } else {
        filterList.splice(0, 0, filterRes);
      }
      const consultantListPayload = {
        data: filterList,
        type: "consultant"
      };
      dispatch(setSavedFilterByType(consultantListPayload));
    }
    dispatch(setSelectedListCategory("consultant"));
    dispatch(setSelectedList(filterRes));
    dispatch(clearFilters({ eliminateMyList: true, skipView: true }));
  };

  const handleEmailChange = (v: any, index: any) => {
    const tempValues = values.map((oldVal, i) => {
      if (i === index) {
        return { ...oldVal, value: { innCode: oldVal?.value?.innCode, email: v?.email, hltId: v?.hltId } };
      } else {
        return oldVal;
      }
    });
    setValues(tempValues);
    dispatch(setInncodes([]));
  };
  const handleSubmit = async () => {
    if (values.length) {
      let payload: any = {
        name: uploadFileDetails?.name,
        mappings: values.map((item: any) => item.value),
        formUpload: false
      };
      if (uploadFileDetails?.id) payload.id = uploadFileDetails?.id;

      const response = await dispatch(updateInncodeUploadList(payload));
      if (response?.payload?.status < responseTypes.success_threshold) {
        const submitedList = response?.payload?.data?.scList;
        const consultantList = cloneDeep(savedFilter.consultant);
        checkIfListExist(consultantList, submitedList);
        dispatch(setCreatedSuccess({ isCreated: true, message: translate("list.uploadInncode.success") }));
      }

      await dispatch(
        fetchMyLists({
          searchText: "",
          page: 0,
          size: SME_PAGE_SIZE
        })
      ).then((res: any) => {
        if (res?.payload?.innCodeGroups[0]?.id) {
          dispatch(
            fetchMyListDetails({
              id: response?.payload?.data?.scList?.id,
              view: "inncodes",
              size: SME_PAGE_SIZE
            })
          );
        }
      });
      if (response?.payload?.data?.id) setSearchParams({ id: response?.payload?.data?.id, tab: "inncodes" });
    } else if (uploadFileDetails?.id)
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          setState: setFeedbackDetails({ shouldShow: false }),
          message: messages.listUploadSuccess,
          variant: "success"
        })
      );
    else
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          setState: setFeedbackDetails({ shouldShow: false }),
          message: messages.listNotCreated
        })
      );

    handleClose();
  };

  const handleDelete = (index: number) => setValues([...values.slice(0, index), ...values.slice(index + 1)]);
  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      fullScreen={isMobileScreen}
      sx={isMobileScreen ? { paddingTop: "76px", zIndex: "5" } : { borderRadius: "30px" }}
      open={openDialog}
      PaperProps={{
        sx: { borderRadius: "12px" }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: tokens.Background.General.Color,
          padding: isMobileScreen ? "16px 16px 24px" : "32px 32px 24px"
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          flexWrap={isMobileScreen ? "wrap-reverse" : "nowrap"}
        >
          <Grid item sx={{ width: "100%" }}>
            <Typography color={tokens.Text.General.PrimaryHeadline} showBold variant="h4">
              Invalid Records
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent sx={dialogContentStyle} id="actionItemsDivMobile">
        <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
          <Box width={isMobileScreen ? "100%" : "40%"} padding={isMobileScreen ? "32px 0 16px" : "auto"}>
            <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
              Below are inncodes or emails that did not match our records. Please update these entries to complete the
              upload process.
            </Typography>
          </Box>
          {!isMobileScreen && <Divider />}
          {!isMobileScreen && (
            <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
              <>
                <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 1 }}>
                  Provided Inncode
                </Typography>
                <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 1 }}>
                  Provided Email
                </Typography>
                <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 1.2 }}>
                  Inncode
                </Typography>
                <Typography variant="body2" color={tokens.Text.General.Tertiary} sx={{ flex: 1.2 }}>
                  User Email (Optional)
                </Typography>
                <Box sx={{ flex: 0.2 }} />
              </>
            </Stack>
          )}

          {values?.map((code: any, index: number) => (
            <React.Fragment key={code.id}>
              <Stack
                direction={isMobileScreen ? "column" : "row"}
                alignItems={isMobileScreen ? "flex-start" : "center"}
                gap={"16px"}
                sx={{
                  padding: isMobileScreen ? "8px 0" : "16px 12px 16px 12px",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: tokens.Background.General.Secondary
                  },
                  "& .close-icon .icon-svg": {
                    display: "none"
                  },
                  "&:hover .close-icon .icon-svg": {
                    display: "block"
                  },
                  flex: 1
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {isMobileScreen && (
                    <Typography variant="body2" color={tokens.Text.General.Secondary}>
                      Uploaded Inncode
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "uppercase" }}
                    color={tokens.Text.General.PrimaryHeadline}
                  >
                    {code?.innCode?.value ? code?.innCode?.value : "-"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {isMobileScreen && (
                    <Typography variant="body2" color={tokens.Text.General.Secondary}>
                      Uploaded User Email
                    </Typography>
                  )}
                  <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
                    {code?.email?.value ? code?.email?.value : "-"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1.2, width: "100%" }}>
                  {isMobileScreen && (
                    <Typography variant="body2" color={tokens.Text.General.Tertiary}>
                      Inncode
                    </Typography>
                  )}
                  <Box>
                    <AutocompleteInncode onSelect={(val: any) => handleChange(val, index)} size="small" />
                  </Box>
                </Box>
                <Box sx={{ flex: 1.2, width: "100%" }}>
                  {isMobileScreen && (
                    <Typography variant="body2" color={tokens.Text.General.Tertiary}>
                      User Email (Optional)
                    </Typography>
                  )}
                  <Box>
                    <AsyncAutocomplete
                      selectedInputValue={code?.value?.email}
                      disabled={!code?.value?.innCode}
                      getUserDetail={(val: any) => handleEmailChange(val, index)}
                      placeholder="Search User"
                      multiple={false}
                      size="small"
                      showIcon={false}
                      searchParams={{ innCode: [code?.value?.innCode] }}
                      isUpdateListType
                    />
                  </Box>
                </Box>
                <Box flex={0.1}></Box>
                {!isMobileScreen ? (
                  <Box
                    sx={{
                      cursor: "pointer",
                      flex: 0.1,
                      minWidth: "20px"
                    }}
                    className={`close-icon`}
                    onClick={() => handleDelete(index)}
                  >
                    <DeleteCircle color={tokens.Icon.General.Color} />
                  </Box>
                ) : (
                  <ButtonLink
                    text="Delete entry"
                    sx={{ color: tokens.Text.General.Color }}
                    textVariant="body2"
                    underline="none"
                    onClick={() => {
                      handleDelete(index);
                    }}
                  />
                )}
              </Stack>
              {isMobileScreen && <Divider sx={{ backgroundColor: tokens.Border.General.Tertiary }} />}
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "24px 32px",
          border: `1px solid ${tokens.Border.General.Tertiary}`,
          borderWidth: "1px 0px 0px 0px",
          justifyContent: isMobileScreen ? "center" : "flex-end"
        }}
      >
        <Button
          disabled={values?.filter((item: any) => item?.value?.innCode).length !== values.length}
          variant="primary"
          label={"Import"}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ImportUpdateDialog;
